// src/wiring/manifest/omnicineAudioResolver.creator.ts
//
// Creator tier capability (transformer):
// - Installs the WAV export audio resolver hook (authority runtime helper)
// - Scoped to OmniCine route only (avoid activating on non-authority pages)
//

import type { CapabilityManifest } from "@/contracts/capabilities/CapabilityManifest";
import { report } from "@/wiring/telemetry/report";

import {
  SYNK_OPS_METRIC_EVENT,
  SYNK_OPS_VIOLATION_EVENT,
  type SynkOpsMetricDetailV1,
  type SynkOpsViolationDetailV1,
} from "@/contracts/ops/OpsEvents";

import { installExportAudioResolver } from "@/bridge/omnicine/export/installExportAudioResolver";

const CAP_ID = "omnicine.audio.resolver" as const;

function safeDispatch(name: string, detail: unknown): void {
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {
    // ignore
  }
}

function emitMetric(name: string, value: number): void {
  const d: SynkOpsMetricDetailV1 = {
    v: 1,
    at: Date.now(),
    kind: "custom",
    name,
    value,
    band: "normal",
    capabilityId: CAP_ID,
  };
  safeDispatch(SYNK_OPS_METRIC_EVENT, d);
}

function emitViolation(reason: string, where = "omnicine.audio.resolver"): void {
  const d: SynkOpsViolationDetailV1 = {
    v: 1,
    at: Date.now(),
    severity: "warning",
    channel: "omnicine.export",
    where,
    reason,
    schema: "omnicine.audio.resolver",
  };
  safeDispatch(SYNK_OPS_VIOLATION_EVENT, d);
}

function isOmniCineRoute(): boolean {
  try {
    return window.location.pathname.startsWith("/omnicine");
  } catch {
    return false;
  }
}

export const OmniCineAudioResolverCreatorManifest: CapabilityManifest = {
  id: CAP_ID,
  version: "1.0.0",
  safety: "transformer",
  tier: "creator",
  requires: [],
  provides: ["omnicine.export.wav.audio_resolve"],
  billing: { metered: false },

  install(): () => void {
    // Guard SSR
    if (typeof window === "undefined") return () => {};

    // Route scope: this capability is authority-side and should only activate on OmniCine.
    if (!isOmniCineRoute()) {
      report("capability.skipped", { id: CAP_ID, reason: "not_omnicine_route" });
      return () => {};
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w: any = window as any;
    w.__synk_capabilities = w.__synk_capabilities || {};

    // Idempotency marker
    if (w.__synk_capabilities.omnicineAudioResolverInstalled) {
      report("capability.warn", { id: CAP_ID, reason: "already_installed" });
      return () => {};
    }

    w.__synk_capabilities.omnicineAudioResolverInstalled = true;

    let uninstall: (() => void) | null = null;

    try {
      uninstall = installExportAudioResolver();
      emitMetric("capability.installed", 1);
      report("capability.installed", { id: CAP_ID, tier: "creator", safety: "transformer" });
    } catch (err) {
      emitViolation("install_failed");
      report("capability.install_failed", {
        id: CAP_ID,
        step: "installExportAudioResolver",
        error: String(err),
      });

      // Roll back idempotency marker on failure
      try {
        delete w.__synk_capabilities.omnicineAudioResolverInstalled;
      } catch {
        // ignore
      }

      return () => {};
    }

    return () => {
      try {
        uninstall?.();
      } catch {
        // ignore
      }

      try {
        if (w.__synk_capabilities) delete w.__synk_capabilities.omnicineAudioResolverInstalled;
      } catch {
        // ignore
      }

      try {
        report("capability.uninstalled", { id: CAP_ID });
      } catch {
        // ignore
      }
    };
  },
};
